import { Injectable, type Provider } from '@angular/core';

import type { Presence } from '../../../core/constants/presence';
import { ok, type ApiResult } from '../../../core/models/api-result';
import type { AutoBookPlan, AutoBookResult } from '../../../core/services/auto-book.service';
import { addDays, type DeskChoice, type PlanVisitDay, type PlanVisitWindow, type VisitMode } from './plan-visit.models';
import { PlanVisitService } from './plan-visit.service';

/**
 * PLAN A VISIT, WITHOUT A SESSION (`/preview/hubs`).
 *
 * Rum Runner Works (`loc-rrw`, the preview office) over five days from
 * today, all open, so the harness reads the same on any day of the week:
 *   - day 2 carries "Working from home", so Undo has a status to put back;
 *   - day 3 already holds desk 2.14 (the green "2.14" on its tile);
 *   - day 4 is refused for desks: the weekly limit, with its reason;
 *   - limits: desks up to 14 days ahead, 2 of 4 desk days left, the team in
 *     Tue, Wed and Thu.
 * Writes land in memory, so a booking shows on the card and Undo takes it back.
 *
 * `?visit=` picks today's state:
 *   (default) booked: desk 2.14 today, matching the preview tile's desk;
 *   open:     nothing planned today, office open ("Plan a visit");
 *   closed:   today past closing at 18:00 ("Plan tomorrow");
 *   in:       in the office today with no desk ("Book a desk").
 */

export type PreviewVisitState = 'booked' | 'open' | 'closed' | 'in';

export function previewVisitState(): PreviewVisitState {
  if (typeof location === 'undefined') return 'booked';
  const match = /[?&]visit=(open|closed|in|booked)\b/.exec(location.hash + location.search);
  return (match?.[1] as PreviewVisitState | undefined) ?? 'booked';
}

export const PREVIEW_VISIT_LOCATION = 'loc-rrw';

const DESKS: readonly DeskChoice[] = [
  { spaceId: 'desk-214', name: 'Desk 2.14', floorName: 'Level 2', isFavourite: true },
  { spaceId: 'desk-216', name: 'Desk 2.16', floorName: 'Level 2', isFavourite: false },
  { spaceId: 'desk-g08', name: 'Desk G.08', floorName: 'Ground', isFavourite: false },
];

const REFUSED = 'You already have 4 desk days that week, and the limit is 4. This day cannot take a desk.';

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function blankDay(date: string, expected: number, pct: number): PlanVisitDay {
  return {
    date,
    state: 'OPEN',
    opensAt: '08:00',
    closesAt: '18:00',
    closedReason: null,
    canBookDesk: true,
    deskRefusal: null,
    deskRefusalReason: null,
    canSetInOffice: true,
    inOfficeRefusalReason: null,
    weeklyDeskRemaining: 2,
    expected,
    capacityPct: pct,
    myDesk: null,
    myInOfficeBookingId: null,
    myStatus: null,
  };
}

function held(date: string, desk: DeskChoice): PlanVisitDay['myDesk'] {
  return { bookingId: `pv-${desk.spaceId}-${date}`, spaceId: desk.spaceId, label: desk.name, floorName: desk.floorName, allDay: true, startTime: '', endTime: '' };
}

/** The harness's five days, as the switch asks, from `today`. */
export function previewVisitWindow(state: PreviewVisitState = previewVisitState(), today = localToday()): PlanVisitWindow {
  const expected = [38, 41, 30, 24, 33];
  const pct = [0.62, 0.8, 0.45, 0.35, 0.55];
  const days = expected.map((n, i) => blankDay(addDays(today, i), n, pct[i]));
  days[1] = { ...days[1], myStatus: 'Working from home' };
  days[2] = { ...days[2], myDesk: held(days[2].date, DESKS[0]) };
  days[3] = {
    ...days[3],
    canBookDesk: false,
    deskRefusal: 'weekly_limit',
    deskRefusalReason: REFUSED,
    weeklyDeskRemaining: 0,
  };
  if (state === 'booked') days[0] = { ...days[0], myDesk: held(days[0].date, DESKS[0]) };
  if (state === 'in') days[0] = { ...days[0], myInOfficeBookingId: `pv-in-${days[0].date}` };
  if (state === 'closed') {
    days[0] = {
      ...days[0],
      state: 'PAST_CLOSING',
      closedReason: 'Closed at 18:00 today',
      canBookDesk: false,
      deskRefusal: 'closed',
      canSetInOffice: false,
      capacityPct: null,
    };
  }
  return {
    locationId: PREVIEW_VISIT_LOCATION,
    locationName: 'Rum Runner Works',
    timezone: 'Europe/London',
    today,
    nextOpenDate: state === 'closed' ? days[1].date : days[0].date,
    todayClosedAt: state === 'closed' ? '18:00' : null,
    limits: {
      deskAdvanceDays: 14,
      allowFutureDeskBookings: true,
      weeklyDeskLimit: 4,
      weeklyDeskUsed: 2,
      weeklyDeskRemaining: 2,
      dailyDeskLimit: null,
      inOfficeAllowed: true,
      inOfficeAdvanceDays: null,
      approvalRequired: false,
      policyUnavailable: false,
    },
    teamDays: ['Tue', 'Wed', 'Thu'],
    days,
  };
}

@Injectable()
export class PlanVisitPreviewService extends PlanVisitService {
  /** The harness's own days, with every write applied. */
  protected state: PlanVisitWindow = previewVisitWindow();

  protected override async fetchWindow(
    locationId: string,
    from: string | null,
  ): Promise<ApiResult<PlanVisitWindow | null>> {
    if (!from) return ok({ ...this.state, locationId });
    // "Next week": five open days from that Monday, nothing held, nothing refused.
    const page = previewVisitWindow('open', from);
    return ok({
      ...page,
      locationId,
      today: this.state.today,
      todayClosedAt: null,
      days: page.days.map(d => ({ ...d, myStatus: null, myDesk: null, canBookDesk: true, deskRefusal: null, deskRefusalReason: null, weeklyDeskRemaining: 4 })),
    });
  }

  protected override async fetchSuggestions(): Promise<DeskChoice[]> {
    return [...DESKS];
  }

  protected override async bookDesks(plans: AutoBookPlan[]): Promise<AutoBookResult[]> {
    return plans.map(plan => {
      const day = this.state.days.find(d => d.date === plan.date);
      if (!day || !day.canBookDesk) {
        return { success: false, date: plan.date, kind: 'desk', reason: 'weekly-limit', error: day?.deskRefusalReason ?? 'That day did not book.' };
      }
      const desk = DESKS.find(d => d.spaceId === plan.spaceId) ?? DESKS[1];
      this.patch(plan.date, { myDesk: held(plan.date, desk), myInOfficeBookingId: null, myStatus: null });
      return { success: true, date: plan.date, kind: 'desk', spaceName: desk.name, spaceId: desk.spaceId, bookingId: `pv-${desk.spaceId}-${plan.date}` };
    });
  }

  protected override async setInOffice(date: string): Promise<{ ok: boolean; error?: string }> {
    const day = this.state.days.find(d => d.date === date);
    if (!day || !day.canSetInOffice) return { ok: false, error: 'The office has already closed on this day' };
    if (!day.myDesk) this.patch(date, { myInOfficeBookingId: `pv-in-${date}`, myStatus: null });
    return { ok: true };
  }

  protected override async cancelBooking(bookingId: string): Promise<boolean> {
    const day = this.state.days.find(d => d.myDesk?.bookingId === bookingId || d.myInOfficeBookingId === bookingId);
    if (day) this.patch(day.date, day.myDesk?.bookingId === bookingId ? { myDesk: null } : { myInOfficeBookingId: null });
    return true;
  }

  protected override async restorePresence(date: string, previous: Presence | null, mode: VisitMode): Promise<boolean> {
    const name = previous === 'home' ? 'Working from home' : previous === 'elsewhere' ? 'Working elsewhere' : previous === 'off' ? 'Out of office' : null;
    if (name) this.patch(date, { myStatus: name, myInOfficeBookingId: null });
    else if (mode === 'status') this.patch(date, { myInOfficeBookingId: null });
    return true;
  }

  private patch(date: string, change: Partial<PlanVisitDay>): void {
    this.state = {
      ...this.state,
      days: this.state.days.map(d => {
        if (d.date !== date) return d;
        const next = { ...d, ...change };
        const wasIn = !!d.myDesk || !!d.myInOfficeBookingId;
        const isIn = !!next.myDesk || !!next.myInOfficeBookingId;
        return { ...next, expected: d.expected + (isIn && !wasIn ? 1 : !isIn && wasIn ? -1 : 0) };
      }),
    };
  }
}

/** Route-level provider: the sheet and every trigger on /preview/hubs read the harness. */
export const PLAN_VISIT_PREVIEW_PROVIDER: Provider = {
  provide: PlanVisitService,
  useClass: PlanVisitPreviewService,
};
